import { supabase } from './supabaseClient';

export interface VerificacaoAcesso {
  temAcesso: boolean;
  tipo?: 'email' | 'codigo';
  mensagem: string;
  acessoId?: string;
}

/**
 * Envia notificação por email quando alguém usa o código da semana
 */
async function notificarUsoCodigoSemanal(
  codigo: string,
  userId: string,
  userName?: string,
  userEmail?: string
) {
  try {
    const response = await fetch(
      `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/notify-code-usage`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          codigo,
          userId,
          userName,
          userEmail,
        }),
      }
    );

    if (!response.ok) {
      console.error('Erro ao enviar notificação de uso do código');
    }
  } catch (error) {
    console.error('Erro ao chamar função de notificação:', error);
  }
}

/**
 * Registra conversão de influencer
 */
async function registrarConversaoInfluencer(
  codigoInfluencer: string, 
  email: string, 
  userId: string | null, 
  tipoConversao: 'lead' | 'cadastro' | 'assinatura'
) {
  try {
    // Registrar conversão
    await supabase
      .from('influencer_conversoes')
      .insert({
        codigo_influencer: codigoInfluencer,
        user_id: userId,
        email: email,
        tipo_conversao: tipoConversao
      });

    // Incrementar contador de conversões
    const { data: influencerData } = await supabase
      .from('acesso_gratuito')
      .select('total_conversoes')
      .eq('codigo_influencer', codigoInfluencer)
      .maybeSingle();

    if (influencerData) {
      await supabase
        .from('acesso_gratuito')
        .update({ 
          total_conversoes: (influencerData.total_conversoes || 0) + 1 
        })
        .eq('codigo_influencer', codigoInfluencer);
    }
  } catch (error) {
    console.error('Erro ao registrar conversão:', error);
  }
}

/**
 * Captura código de referência da URL e salva no localStorage
 */
export function capturarCodigoReferencia(): string | null {
  if (typeof window === 'undefined') return null;
  
  const urlParams = new URLSearchParams(window.location.search);
  const refCode = urlParams.get('ref');
  
  if (refCode) {
    // Salvar no localStorage para usar depois no cadastro
    localStorage.setItem('codigo_referencia', refCode);
    return refCode;
  }
  
  // Tentar recuperar do localStorage
  return localStorage.getItem('codigo_referencia');
}

/**
 * Limpa código de referência do localStorage
 */
export function limparCodigoReferencia() {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('codigo_referencia');
  }
}

/**
 * Verifica se um e-mail tem acesso gratuito
 */
export async function verificarAcessoPorEmail(email: string): Promise<VerificacaoAcesso> {
  try {
    const { data, error } = await supabase
      .from('acesso_gratuito')
      .select('*')
      .eq('tipo', 'email')
      .eq('valor', email.toLowerCase())
      .eq('ativo', true)
      .eq('usado', false)
      .maybeSingle();

    if (error) {
      console.error('Erro ao verificar acesso por e-mail:', error);
      return { temAcesso: false, mensagem: 'Erro ao verificar acesso' };
    }

    if (!data) {
      return { temAcesso: false, mensagem: 'E-mail não possui acesso gratuito' };
    }

    // Verificar expiração
    if (data.expira_em && new Date(data.expira_em) < new Date()) {
      return { temAcesso: false, mensagem: 'Acesso gratuito expirado' };
    }

    return {
      temAcesso: true,
      tipo: 'email',
      mensagem: 'Acesso gratuito concedido por e-mail!',
      acessoId: data.id
    };
  } catch (error) {
    console.error('Erro ao verificar acesso:', error);
    return { temAcesso: false, mensagem: 'Erro ao verificar acesso' };
  }
}

/**
 * Verifica e aplica um código de convite
 */
export async function aplicarCodigoConvite(codigo: string, userId: string, email?: string): Promise<VerificacaoAcesso> {
  try {
    // Verificar se usuário já tem acesso gratuito
    const { data: profileCheck } = await supabase
      .from('profiles')
      .select('acesso_gratuito, nome, email')
      .eq('id', userId)
      .maybeSingle();

    if (profileCheck?.acesso_gratuito) {
      return { 
        temAcesso: false, 
        mensagem: 'Você já possui acesso gratuito ativo' 
      };
    }

    // Buscar código (individual ou semanal)
    const { data: codigoData, error: codigoError } = await supabase
      .from('acesso_gratuito')
      .select('*')
      .eq('tipo', 'codigo')
      .eq('valor', codigo.toUpperCase())
      .eq('ativo', true)
      .maybeSingle();

    if (codigoError) {
      console.error('Erro ao buscar código:', codigoError);
      return { temAcesso: false, mensagem: 'Erro ao verificar código' };
    }

    if (!codigoData) {
      return { temAcesso: false, mensagem: 'Código inválido ou já utilizado' };
    }

    // Verificar expiração
    if (codigoData.expira_em && new Date(codigoData.expira_em) < new Date()) {
      return { temAcesso: false, mensagem: 'Código expirado' };
    }

    const ehCodigoSemanal = codigoData.tipo_codigo === 'semanal';

    // Se for código semanal, verificar se usuário já usou
    if (ehCodigoSemanal) {
      const { data: jaUsou } = await supabase
        .from('codigo_semanal_usos')
        .select('id')
        .eq('acesso_gratuito_id', codigoData.id)
        .eq('user_id', userId)
        .maybeSingle();

      if (jaUsou) {
        return { temAcesso: false, mensagem: 'Você já utilizou este código' };
      }
    } else {
      // Código individual - verificar se já foi usado
      if (codigoData.usado) {
        return { temAcesso: false, mensagem: 'Código já utilizado' };
      }
    }

    // Registrar uso do código
    if (ehCodigoSemanal) {
      // Código semanal - registrar uso sem bloquear o código
      const { error: usoError } = await supabase
        .from('codigo_semanal_usos')
        .insert({
          acesso_gratuito_id: codigoData.id,
          user_id: userId
        });

      if (usoError) {
        console.error('Erro ao registrar uso do código semanal:', usoError);
        return { temAcesso: false, mensagem: 'Erro ao aplicar código' };
      }
    } else {
      // Código individual - marcar como usado
      const { error: updateError } = await supabase
        .from('acesso_gratuito')
        .update({
          usado: true,
          usado_por: userId,
          usado_em: new Date().toISOString()
        })
        .eq('id', codigoData.id);

      if (updateError) {
        console.error('Erro ao marcar código como usado:', updateError);
        return { temAcesso: false, mensagem: 'Erro ao aplicar código' };
      }
    }

    // Calcular data de expiração (9 dias a partir de agora)
    const dataExpiracao = new Date();
    dataExpiracao.setDate(dataExpiracao.getDate() + 9);

    // Atualizar perfil do usuário
    const { error: profileError } = await supabase
      .from('profiles')
      .update({
        acesso_gratuito: true,
        acesso_gratuito_codigo: codigo.toUpperCase(),
        acesso_gratuito_concedido_em: new Date().toISOString(),
        acesso_gratuito_expira_em: dataExpiracao.toISOString(),
        codigo_influencer_origem: codigoData.codigo_influencer || null
      })
      .eq('id', userId);

    if (profileError) {
      console.error('Erro ao atualizar perfil:', profileError);
      
      // Reverter registro se for código semanal
      if (ehCodigoSemanal) {
        await supabase
          .from('codigo_semanal_usos')
          .delete()
          .eq('acesso_gratuito_id', codigoData.id)
          .eq('user_id', userId);
      } else {
        // Reverter marcação do código individual
        await supabase
          .from('acesso_gratuito')
          .update({
            usado: false,
            usado_por: null,
            usado_em: null
          })
          .eq('id', codigoData.id);
      }
      
      return { temAcesso: false, mensagem: 'Erro ao conceder acesso' };
    }

    // Registrar conversão se for código de influencer
    if (codigoData.codigo_influencer && email) {
      await registrarConversaoInfluencer(codigoData.codigo_influencer, email, userId, 'cadastro');
    }

    // 🎉 ENVIAR NOTIFICAÇÃO POR EMAIL SE FOR CÓDIGO SEMANAL
    if (ehCodigoSemanal) {
      const userName = profileCheck?.nome || 'Usuário';
      const userEmail = email || profileCheck?.email || '';
      
      // Chamar função de notificação (não aguarda para não bloquear o fluxo)
      notificarUsoCodigoSemanal(codigo.toUpperCase(), userId, userName, userEmail);
    }

    return {
      temAcesso: true,
      tipo: 'codigo',
      mensagem: 'Código aplicado com sucesso! Acesso gratuito concedido por 9 dias!'
    };
  } catch (error) {
    console.error('Erro ao aplicar código:', error);
    return { temAcesso: false, mensagem: 'Erro ao aplicar código' };
  }
}

/**
 * Concede acesso gratuito por e-mail após cadastro
 */
export async function concederAcessoPorEmail(email: string, userId: string): Promise<boolean> {
  try {
    // Capturar código de referência se existir
    const codigoReferencia = capturarCodigoReferencia();

    // Verificar se e-mail tem acesso
    const verificacao = await verificarAcessoPorEmail(email);
    
    if (!verificacao.temAcesso || !verificacao.acessoId) {
      // Se não tem acesso por e-mail, mas tem código de referência, salvar origem
      if (codigoReferencia) {
        await supabase
          .from('profiles')
          .update({
            origem_referencia: `ref_${codigoReferencia}`,
            codigo_influencer_origem: codigoReferencia
          })
          .eq('id', userId);

        // Registrar conversão de lead
        await registrarConversaoInfluencer(codigoReferencia, email, userId, 'lead');
        
        // Limpar código após uso
        limparCodigoReferencia();
      }
      return false;
    }

    // Marcar como usado
    const { error: updateError } = await supabase
      .from('acesso_gratuito')
      .update({
        usado: true,
        usado_por: userId,
        usado_em: new Date().toISOString()
      })
      .eq('id', verificacao.acessoId);

    if (updateError) {
      console.error('Erro ao marcar acesso como usado:', updateError);
      return false;
    }

    // Calcular data de expiração (9 dias a partir de agora)
    const dataExpiracao = new Date();
    dataExpiracao.setDate(dataExpiracao.getDate() + 9);

    // Atualizar perfil
    const updateData: any = {
      acesso_gratuito: true,
      acesso_gratuito_concedido_em: new Date().toISOString(),
      acesso_gratuito_expira_em: dataExpiracao.toISOString()
    };

    // Adicionar código de referência se existir
    if (codigoReferencia) {
      updateData.origem_referencia = `ref_${codigoReferencia}`;
      updateData.codigo_influencer_origem = codigoReferencia;
    }

    const { error: profileError } = await supabase
      .from('profiles')
      .update(updateData)
      .eq('id', userId);

    if (profileError) {
      console.error('Erro ao atualizar perfil:', profileError);
      return false;
    }

    // Registrar conversão se for código de influencer
    if (codigoReferencia) {
      await registrarConversaoInfluencer(codigoReferencia, email, userId, 'cadastro');
      limparCodigoReferencia();
    }

    return true;
  } catch (error) {
    console.error('Erro ao conceder acesso por e-mail:', error);
    return false;
  }
}

/**
 * Verifica se usuário tem acesso gratuito ativo
 */
export async function verificarAcessoUsuario(userId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('acesso_gratuito, acesso_gratuito_expira_em')
      .eq('id', userId)
      .maybeSingle();

    if (error || !data) {
      return false;
    }

    // Se não tem acesso gratuito, retornar false
    if (!data.acesso_gratuito) {
      return false;
    }

    // Verificar se o acesso expirou
    if (data.acesso_gratuito_expira_em) {
      const dataExpiracao = new Date(data.acesso_gratuito_expira_em);
      const agora = new Date();

      if (agora > dataExpiracao) {
        // Acesso expirado - desativar automaticamente
        await supabase
          .from('profiles')
          .update({ acesso_gratuito: false })
          .eq('id', userId);
        
        return false;
      }
    }

    return true;
  } catch (error) {
    console.error('Erro ao verificar acesso do usuário:', error);
    return false;
  }
}

/**
 * Registra conversão de assinatura (quando usuário paga)
 */
export async function registrarConversaoAssinatura(userId: string, email: string) {
  try {
    const { data: profile } = await supabase
      .from('profiles')
      .select('codigo_influencer_origem')
      .eq('id', userId)
      .maybeSingle();

    if (profile?.codigo_influencer_origem) {
      await registrarConversaoInfluencer(
        profile.codigo_influencer_origem, 
        email, 
        userId, 
        'assinatura'
      );
    }
  } catch (error) {
    console.error('Erro ao registrar conversão de assinatura:', error);
  }
}
